/**
 * Search store — отдельный persistent state для /process/search.
 *
 * Search enrichment запускается только с модулем search_enrichment.
 * Результат пишется в DatasetRow.enriched_data.
 *
 * Block S1 (Search v2): добавлены priority_domains (список доменов) и
 * tierKey (economy/optimal/max). Pipeline start payload включает оба.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type SearchTierKey = 'economy' | 'optimal' | 'max';

interface SearchState {
  datasetId: string | null;
  selectedColumns: string[];
  outputLanguage: string;
  tierKey: SearchTierKey;
  priorityDomains: string[];
  currentPipelineId: string | null;
  /** UX-J2 (2026-07-28): row scope for test runs (1/3/5). 0 = All. */
  isTestRun: boolean;
  testRunLimit: number;
  /**
   * Fix §7 (2026-09-17): затирать готовые результаты. false (default) —
   * строки с готовым результатом search_v2 пропускаются обычным запуском.
   */
  overwriteExisting: boolean;

  setDatasetId: (id: string | null) => void;
  setSelectedColumns: (cols: string[]) => void;
  toggleColumn: (col: string) => void;
  setOutputLanguage: (lang: string) => void;
  setTierKey: (t: SearchTierKey) => void;
  setPriorityDomains: (domains: string[]) => void;
  addPriorityDomain: (host: string) => void;
  removePriorityDomain: (host: string) => void;
  setCurrentPipelineId: (id: string | null) => void;
  setIsTestRun: (v: boolean) => void;
  setTestRunLimit: (n: number) => void;
  setOverwriteExisting: (v: boolean) => void;
  reset: () => void;
}

const DEFAULTS = {
  datasetId: null as string | null,
  selectedColumns: [] as string[],
  outputLanguage: 'Russian',
  tierKey: 'optimal' as SearchTierKey,
  priorityDomains: [] as string[],
  currentPipelineId: null as string | null,
  isTestRun: false,
  // M1 (2026-09-10): лимит тест-прогона — default 2, максимум 3 (backend le=3).
  testRunLimit: 2,
  // §7: по умолчанию НЕ затирать готовые результаты поиска v2.
  overwriteExisting: false,
};

// M1: тест-лимит ограничен 1..3 на бэкенде (schema le=3); UI предлагает
// 2/3 (опции скоупа). Клампим и тут, включая восстановленное из localStorage
// значение (старые сессии могли сохранить 1/5/20).
const clampTestRunLimit = (n: number): number => {
  const v = Number.isFinite(n) ? Math.round(n) : 2;
  if (v >= 3) return 3;
  return 2;
};

export const useSearchStore = create<SearchState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      setDatasetId: (id) => set({ datasetId: id }),
      setSelectedColumns: (cols) => set({ selectedColumns: cols }),
      toggleColumn: (col) =>
        set((state) => ({
          selectedColumns: state.selectedColumns.includes(col)
            ? state.selectedColumns.filter((c) => c !== col)
            : [...state.selectedColumns, col],
        })),
      setOutputLanguage: (lang) => set({ outputLanguage: lang }),
      setTierKey: (t) => set({ tierKey: t }),
      setPriorityDomains: (domains) => set({ priorityDomains: domains }),
      addPriorityDomain: (host) =>
        set((state) => {
          const clean = host.trim().toLowerCase();
          if (!clean) return state;
          if (state.priorityDomains.includes(clean)) return state;
          return { priorityDomains: [...state.priorityDomains, clean] };
        }),
      removePriorityDomain: (host) =>
        set((state) => ({
          priorityDomains: state.priorityDomains.filter((d) => d !== host),
        })),
      setCurrentPipelineId: (id) => set({ currentPipelineId: id }),
      setIsTestRun: (v) => set({ isTestRun: v }),
      setTestRunLimit: (n) => set({ testRunLimit: clampTestRunLimit(n) }),
      setOverwriteExisting: (v) => set({ overwriteExisting: v }),
      reset: () => set({ ...DEFAULTS }),
    }),
    {
      name: 'cs_search_config',
      onRehydrateStorage: () => (state) => {
        if (state && state.testRunLimit !== clampTestRunLimit(state.testRunLimit)) {
          state.testRunLimit = clampTestRunLimit(state.testRunLimit);
        }
      },
      // Fix 2026-09-19 (TZ A/B): isTestRun and overwriteExisting describe the
      // mode of ONE concrete run, not a session setting. isTestRun used to be
      // persisted - once enabled it silently stayed on across sessions, so the
      // user kept launching test runs that never save results (by design) and
      // the table looked frozen. After reload the mode is always normal.
      partialize: (state) => ({
        outputLanguage: state.outputLanguage,
        tierKey: state.tierKey,
        priorityDomains: state.priorityDomains,
        testRunLimit: state.testRunLimit,
      }),
    },
  ),
);
